/**
 * Admin editor for the servicing picklists (migration 2026-10-06-320): Subject / Task Type,
 * Activity Type, Related Goal / Session. Add, rename, reorder, retire/restore, and clear the
 * "placeholder" flag once the business has supplied its own value. Values are never deleted, so
 * history keeps its labels. The database enforces admin-only writes; this screen is only shown to
 * admins.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createLookupValue,
  getLookupValues,
  LOOKUP_LABELS,
  updateLookupValue,
} from "../../services/interactionServicing.service";
import type {
  InteractionLookupList,
  InteractionLookupValue,
} from "../../types/interaction.types";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { LOOKUP_QUERY_KEY } from "./InteractionForm";

const LISTS: InteractionLookupList[] = [
  "subjects",
  "activity_types",
  "goal_sessions",
];

const errText = (e: unknown) =>
  e && typeof e === "object" && "message" in e
    ? String((e as { message: unknown }).message)
    : "The change was not saved.";

const ListEditor: React.FC<{ list: InteractionLookupList }> = ({ list }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [newLabel, setNewLabel] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const { data: values, isLoading } = useQuery({
    queryKey: [LOOKUP_QUERY_KEY, list],
    queryFn: () => getLookupValues(list),
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [LOOKUP_QUERY_KEY] });

  const add = useMutation({
    mutationFn: () => {
      const max = Math.max(0, ...(values || []).map((v) => v.sort_order));
      return createLookupValue(list, newLabel, Math.min(max + 10, 899));
    },
    onSuccess: () => {
      setNewLabel("");
      refresh();
    },
    onError: (e) => showToast("error", errText(e)),
  });
  const patch = useMutation({
    mutationFn: ({
      v,
      changes,
    }: {
      v: InteractionLookupValue;
      changes: Parameters<typeof updateLookupValue>[2];
    }) => updateLookupValue(list, v.id, changes),
    onSuccess: () => refresh(),
    onError: (e) => showToast("error", errText(e)),
  });

  return (
    <div className="space-y-3" data-testid={`lookup-editor-${list}`}>
      <h4 className="font-semibold text-gray-900">{LOOKUP_LABELS[list]}</h4>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (values || []).length === 0 ? (
        <p className="text-sm text-gray-500">
          No values yet.
          {list === "goal_sessions"
            ? " The field stays hidden on the activity form until a value exists."
            : ""}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border rounded-md">
          {(values || []).map((v) => (
            <li
              key={v.id}
              className="flex flex-wrap items-center gap-2 px-3 py-2"
            >
              <input
                className="flex-1 min-w-[10rem] px-2 py-1 border border-gray-300 rounded"
                value={drafts[v.id] ?? v.label}
                onChange={(e) =>
                  setDrafts((d) => ({ ...d, [v.id]: e.target.value }))
                }
              />
              {drafts[v.id] !== undefined && drafts[v.id] !== v.label && (
                <Button
                  size="sm"
                  onClick={() =>
                    patch.mutate(
                      { v, changes: { label: drafts[v.id] } },
                      {
                        onSuccess: () =>
                          setDrafts((d) => {
                            const n = { ...d };
                            delete n[v.id];
                            return n;
                          }),
                      },
                    )
                  }
                >
                  Save
                </Button>
              )}
              {v.is_placeholder && (
                <button
                  className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                  title="Placeholder value: click to confirm it as a business value"
                  onClick={() =>
                    patch.mutate({ v, changes: { is_placeholder: false } })
                  }
                >
                  Placeholder · confirm
                </button>
              )}
              <button
                className={`text-xs ${v.is_active ? "text-red-600" : "text-green-700"}`}
                onClick={() =>
                  patch.mutate({ v, changes: { is_active: !v.is_active } })
                }
              >
                {v.is_active ? "Retire" : "Restore"}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 px-2 py-1 border border-gray-300 rounded"
          placeholder={`Add a ${LOOKUP_LABELS[list]} value`}
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          data-testid={`lookup-new-${list}`}
        />
        <Button
          size="sm"
          disabled={!newLabel.trim()}
          isLoading={add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

const ServicingListsManager: React.FC = () => (
  <div className="space-y-6" data-testid="servicing-lists-manager">
    <p className="text-sm text-gray-600">
      Values marked “Placeholder” were seeded because the business list (CCT
      task list, PDF §6.8) was not supplied. Replace or confirm them. Retired
      values stay on past activity but are no longer offered.
    </p>
    {LISTS.map((l) => (
      <ListEditor key={l} list={l} />
    ))}
  </div>
);

export default ServicingListsManager;
